import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Plus, Pencil, Trash2, RefreshCw, ArrowLeft, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { dynamicDocTypesApi, dynamicDocsApi } from '../services/dynamicDocTypesApi';
import DynamicFieldsSection from '../components/custom-fields/DynamicFieldsSection';
import CustomFieldCell from '../components/custom-fields/CustomFieldCell';
import useCustomFields from '../hooks/useCustomFields';
import { clientScriptsApi } from '../services/clientScriptsApi';
import { runClientScripts } from '../utils/clientScriptRunner';
import logger from '../utils/logger';

/** Render a single form input based on a DynamicDocTypeField definition. */
const FieldInput = ({ field, value, onChange, linkOptions }) => {
  const v = value ?? '';
  const testId = `field-${field.fieldname}`;
  switch (field.fieldtype) {
    case 'textarea':
      return (
        <Textarea
          value={v}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          data-testid={testId}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case 'date':
      return (
        <Input
          type="date"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case 'datetime':
      return (
        <Input
          type="datetime-local"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case 'boolean':
      return (
        <Checkbox
          checked={!!value}
          onCheckedChange={(checked) => onChange(!!checked)}
          data-testid={testId}
        />
      );
    case 'select':
      return (
        <Select value={v} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}><SelectValue placeholder="Select…" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'email':
      return (
        <Input
          type="email"
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
    case 'link':
      return (
        <Select value={v} onValueChange={onChange}>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder={`Select ${field.link_doctype}…`} />
          </SelectTrigger>
          <SelectContent>
            {(linkOptions || []).length === 0 ? (
              <div className="px-2 py-1.5 text-xs text-gray-400">
                No {field.link_doctype} records yet
              </div>
            ) : (
              (linkOptions || []).map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      );
    default: // text
      return (
        <Input
          value={v}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
        />
      );
  }
};

/** Format value for list display */
const formatCell = (value, fieldtype) => {
  if (value === null || value === undefined || value === '') return '—';
  if (fieldtype === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

const DynamicDocList = () => {
  const { doctypeName } = useParams();
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [customData, setCustomData] = useState({});
  const [linkOptions, setLinkOptions] = useState({});
  const [clientScripts, setClientScripts] = useState([]);
  const [saving, setSaving] = useState(false);

  // Custom-field columns (Frappe-style) shown in the list view
  const { fields: listCustomFields } = useCustomFields(doctypeName, { listViewOnly: true });

  const loadDefinition = useCallback(async () => {
    try {
      const r = await dynamicDocTypesApi.getByName(doctypeName);
      setDefinition(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'DocType not found');
      navigate('/config/doctype-builder');
    }
  }, [doctypeName, navigate]);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const r = await dynamicDocsApi.list(doctypeName);
      setRecords(r.data || []);
    } catch (err) {
      logger.error('Failed to load records', err);
      toast.error(err.response?.data?.detail || 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [doctypeName]);

  useEffect(() => {
    loadDefinition();
  }, [loadDefinition]);

  useEffect(() => {
    if (definition) loadRecords();
  }, [definition, loadRecords]);

  // Fetch dropdown options for every Link field's target DocType
  useEffect(() => {
    if (!definition) return;
    const targets = [...new Set(
      (definition.fields || [])
        .filter(f => f.fieldtype === 'link' && f.link_doctype)
        .map(f => f.link_doctype)
    )];
    targets.forEach(async (t) => {
      try {
        const r = await dynamicDocsApi.linkOptions(t);
        setLinkOptions(prev => ({ ...prev, [t]: r.data || [] }));
      } catch (err) {
        logger.error(`Failed to load link options for ${t}`, err);
      }
    });
  }, [definition]);

  // Load active client scripts for this doctype
  useEffect(() => {
    if (!doctypeName) return;
    clientScriptsApi.active(doctypeName)
      .then(r => setClientScripts(r.data || []))
      .catch(() => { /* scripts are optional */ });
  }, [doctypeName]);

  const listFields = useMemo(() => {
    if (!definition) return [];
    const inList = (definition.fields || []).filter(f => f.in_list_view);
    // If admin didn't mark any, fall back to first 4 fields
    return inList.length > 0 ? inList : (definition.fields || []).slice(0, 4);
  }, [definition]);

  const buildInitial = useCallback(() => {
    const values = {};
    (definition?.fields || []).forEach(f => {
      values[f.fieldname] = f.default ?? (f.fieldtype === 'boolean' ? false : '');
    });
    return values;
  }, [definition]);

  const openCreate = () => {
    setEditingDocId(null);
    const initial = buildInitial();
    const { doc } = runClientScripts(clientScripts, 'onload', initial);
    setFormValues(doc);
    setCustomData({});
    setDialogOpen(true);
  };

  const openEdit = (record) => {
    setEditingDocId(record.doc_id);
    const values = {};
    (definition?.fields || []).forEach(f => {
      values[f.fieldname] = record[f.fieldname] ?? (f.fieldtype === 'boolean' ? false : '');
    });
    const { doc } = runClientScripts(clientScripts, 'onload', values);
    setFormValues(doc);
    setCustomData(record.custom_data || {});
    setDialogOpen(true);
  };

  const handleSave = async () => {
    // Client Scripts: validate hook can block the save
    const validation = runClientScripts(clientScripts, 'validate', formValues);
    if (validation.blocked) {
      (validation.errors.length ? validation.errors : ['Validation failed']).forEach(e => toast.error(e));
      return;
    }
    // before_save hook can mutate the doc
    const { doc: effective } = runClientScripts(clientScripts, 'before_save', validation.doc);
    setFormValues(effective);

    // Strip empties so backend validation applies defaults properly
    const payload = {};
    Object.entries(effective).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) payload[k] = v;
    });
    if (customData && Object.keys(customData).length > 0) {
      payload.custom_data = customData;
    }

    setSaving(true);
    try {
      if (editingDocId) {
        await dynamicDocsApi.update(doctypeName, editingDocId, payload);
        toast.success('Record updated');
      } else {
        await dynamicDocsApi.create(doctypeName, payload);
        toast.success('Record created');
      }
      setDialogOpen(false);
      await loadRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Delete record "${record.doc_id}"?`)) return;
    try {
      await dynamicDocsApi.delete(doctypeName, record.doc_id);
      toast.success('Record deleted');
      await loadRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  if (!definition) {
    return <Layout><div className="p-6">Loading DocType…</div></Layout>;
  }

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="dynamic-doc-list-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/config/doctype-builder"><ArrowLeft className="w-4 h-4" /></Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-stone-700" /> {definition.label}
              </h1>
              <p className="text-gray-500 text-sm">
                {definition.description || (
                  <span className="italic">Custom DocType · {definition.doctype_name}</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadRecords} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={openCreate}
              className="bg-stone-800 hover:bg-stone-700"
              data-testid="new-record-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New {definition.label}
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Records ({records.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading…</div>
            ) : records.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No records yet. Create the first one.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    {listFields.map(f => (
                      <TableHead key={f.fieldname}>{f.label}</TableHead>
                    ))}
                    {listCustomFields.map(f => (
                      <TableHead key={`cf-${f.fieldname}`}>{f.label}</TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {records.map(r => (
                    <TableRow key={r.doc_id} data-testid={`record-row-${r.doc_id}`}>
                      <TableCell className="font-mono text-sm">{r.doc_id}</TableCell>
                      {listFields.map(f => (
                        <TableCell key={f.fieldname}>
                          {formatCell(r[f.fieldname], f.fieldtype)}
                        </TableCell>
                      ))}
                      {listCustomFields.map(f => (
                        <TableCell key={`cf-${f.fieldname}`}>
                          <CustomFieldCell field={f} value={r.custom_data?.[f.fieldname]} />
                        </TableCell>
                      ))}
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(r)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(r)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* --- Record Dialog --- */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDocId ? `Edit ${definition.label} · ${editingDocId}` : `New ${definition.label}`}
              </DialogTitle>
              <DialogDescription>
                All fields marked with <span className="text-red-600">*</span> are required.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              {(definition.fields || []).map(f => (
                <div
                  key={f.fieldname}
                  className={f.fieldtype === 'textarea' ? 'col-span-2' : ''}
                >
                  <Label className="flex items-center gap-1">
                    {f.label}
                    {f.required && <span className="text-red-600">*</span>}
                  </Label>
                  <FieldInput
                    field={f}
                    value={formValues[f.fieldname]}
                    onChange={(val) => setFormValues(p => ({ ...p, [f.fieldname]: val }))}
                    linkOptions={f.fieldtype === 'link' ? linkOptions[f.link_doctype] : undefined}
                  />
                  {f.help_text && (
                    <p className="text-xs text-gray-500 mt-1">{f.help_text}</p>
                  )}
                </div>
              ))}
            </div>

            {/* Frappe-style custom fields layered on top of the native definition */}
            <DynamicFieldsSection
              doctype={doctypeName}
              values={customData}
              onChange={setCustomData}
            />

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-stone-800 hover:bg-stone-700"
                data-testid="save-record-btn"
              >
                {saving ? 'Saving…' : (editingDocId ? 'Save Changes' : 'Create Record')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DynamicDocList;
